/**
 * UI State Store
 * Zustand store for UI state management
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { UI } from '@/lib/constants';

// Toast notification type
export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

// Modal state type
interface ModalState {
  isOpen: boolean;
  data?: unknown;
}

export interface UIState {
  // Sidebar
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Theme (always light for this project)
  theme: 'light';

  // Toasts
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;

  // Modals
  modals: Record<string, ModalState>;
  openModal: (id: string, data?: unknown) => void;
  closeModal: (id: string) => void;
  isModalOpen: (id: string) => boolean;
  getModalData: (id: string) => unknown | undefined;

  // Mobile menu
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  toggleMobileMenu: () => void;

  // Page title
  pageTitle: string;
  setPageTitle: (title: string) => void;

  // Breadcrumbs
  breadcrumbs: Array<{ label: string; href?: string }>;
  setBreadcrumbs: (breadcrumbs: Array<{ label: string; href?: string }>) => void;
}

// Generate unique toast ID
let toastIdCounter = 0;
const generateToastId = () => `toast-${++toastIdCounter}-${Date.now()}`;

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Initial State
      sidebarOpen: true,
      sidebarCollapsed: false,
      theme: 'light',
      toasts: [],
      modals: {},
      mobileMenuOpen: false,
      pageTitle: '',
      breadcrumbs: [],

      // Sidebar Actions
      toggleSidebar: () =>
        set((state) => ({
          sidebarOpen: !state.sidebarOpen,
        })),

      setSidebarOpen: (open) => set({ sidebarOpen: open }),

      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

      // Toast Actions
      addToast: (toast) => {
        const id = generateToastId();
        const newToast: Toast = {
          ...toast,
          id,
          duration: toast.duration ?? UI.TOAST_DURATION,
        };

        set((state) => ({
          toasts: [...state.toasts, newToast],
        }));

        // Auto-remove toast after duration
        if (newToast.duration && newToast.duration > 0) {
          setTimeout(() => {
            get().removeToast(id);
          }, newToast.duration);
        }

        return id;
      },

      removeToast: (id) =>
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        })),

      clearToasts: () => set({ toasts: [] }),

      // Modal Actions
      openModal: (id, data) =>
        set((state) => ({
          modals: {
            ...state.modals,
            [id]: { isOpen: true, data },
          },
        })),

      closeModal: (id) =>
        set((state) => ({
          modals: {
            ...state.modals,
            [id]: { isOpen: false, data: undefined },
          },
        })),

      isModalOpen: (id) => !!get().modals[id]?.isOpen,

      getModalData: (id) => get().modals[id]?.data,

      // Mobile Menu Actions
      setMobileMenuOpen: (open) => set({ mobileMenuOpen: open }),

      toggleMobileMenu: () =>
        set((state) => ({
          mobileMenuOpen: !state.mobileMenuOpen,
        })),

      // Page Title Actions
      setPageTitle: (title) => set({ pageTitle: title }),

      // Breadcrumbs Actions
      setBreadcrumbs: (breadcrumbs) => set({ breadcrumbs }),
    }),
    {
      name: 'ui-storage',
      partialize: (state) => ({
        sidebarOpen: state.sidebarOpen,
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
      }),
    }
  )
);

// UI state selectors
export const selectSidebarOpen = (state: UIState) => state.sidebarOpen;
export const selectSidebarCollapsed = (state: UIState) => state.sidebarCollapsed;
export const selectToasts = (state: UIState) => state.toasts;
export const selectMobileMenuOpen = (state: UIState) => state.mobileMenuOpen;
export const selectPageTitle = (state: UIState) => state.pageTitle;
export const selectBreadcrumbs = (state: UIState) => state.breadcrumbs;

// Toast helper functions
export const toast = {
  success: (title: string, message?: string, duration?: number) => {
    useUIStore.getState().addToast({
      type: 'success',
      title,
      message,
      duration,
    });
  },

  error: (title: string, message?: string, duration?: number) => {
    useUIStore.getState().addToast({
      type: 'error',
      title,
      message,
      duration: duration ?? 8000, // Errors stay longer
    });
  },

  warning: (title: string, message?: string, duration?: number) => {
    useUIStore.getState().addToast({
      type: 'warning',
      title,
      message,
      duration,
    });
  },

  info: (title: string, message?: string, duration?: number) => {
    useUIStore.getState().addToast({
      type: 'info',
      title,
      message,
      duration,
    });
  },
};
